import React, { useMemo } from "react";
import {
  FaCalendarAlt,
  FaClock,
  FaLayerGroup,
  FaRegCalendarCheck,
} from "react-icons/fa";

import Reveal from "../../../components/Reveal/Reveal";

const DAY_LABELS = [
  "Lundi",
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi",
  "Dimanche",
];

const DAY_REGEX =
  /^(lundi|mardi|mercredi|jeudi|vendredi|samedi|dimanche)\b/i;

const colorMap = {
  "bg-green-400": "from-emerald-500 to-teal-400",
  "bg-red-400": "from-red-500 to-rose-400",
  "bg-blue-400": "from-blue-500 to-sky-400",
  "bg-yellow-400": "from-yellow-500 to-amber-400",
  "bg-orange-400": "from-orange-500 to-amber-400",
  "bg-purple-400": "from-purple-500 to-fuchsia-400",
  "bg-pink-400": "from-pink-500 to-rose-400",
  "bg-indigo-400": "from-indigo-500 to-blue-400",
  "bg-gray-400": "from-slate-500 to-slate-400",
  "bg-teal-400": "from-teal-500 to-cyan-400",
  "bg-black": "from-slate-950 to-slate-700",
};

const getColorGradient = (color) =>
  colorMap[color] || colorMap["bg-orange-400"];

const tokenizeRanges = (text) =>
  String(text || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const getTeachers = (group) =>
  [group?.enseignant1, group?.enseignant2].filter(Boolean).join(" & ");

const timeToMinutes = (value = "") => {
  const match = String(value).match(/(\d{1,2})\s*(?:h|H|:)?\s*(\d{0,2})/);

  if (!match) return 24 * 60 + 1;

  const hours = Math.min(23, Math.max(0, parseInt(match[1], 10)));
  const minutes = match[2] ? Math.min(59, parseInt(match[2], 10)) : 0;

  return hours * 60 + minutes;
};

const normalizeDay = (day = "") => {
  const clean = String(day)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  const found = DAY_LABELS.find(
    (label) =>
      label
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase() === clean
  );

  return found || "";
};

const parseHoraire = (raw = "") => {
  const value = String(raw).trim();

  if (!value) return null;

  const dayMatch = value.match(DAY_REGEX);

  if (dayMatch) {
    const day = normalizeDay(dayMatch[1]);
    const time = value.slice(dayMatch[0].length).trim();

    return {
      day,
      time: time || "—",
      startMin: timeToMinutes(time),
    };
  }

  const matchingDay = DAY_LABELS.find((day) =>
    value.toLowerCase().startsWith(day.toLowerCase())
  );

  if (!matchingDay) return null;

  const time = value.slice(matchingDay.length).trim();

  return {
    day: matchingDay,
    time: time || "—",
    startMin: timeToMinutes(time),
  };
};

const getTodayLabel = () => {
  const jsDay = new Date().getDay();

  return DAY_LABELS[(jsDay + 6) % 7];
};

const SkeletonDay = () => (
  <div className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
    <div className="h-7 w-40 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
    <div className="mt-5 space-y-3">
      <div className="h-16 w-full animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
      <div className="h-16 w-full animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
      <div className="h-16 w-4/5 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
    </div>
  </div>
);

const EmptyState = () => (
  <div className="col-span-full rounded-[2rem] border border-dashed border-slate-300 bg-white/75 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
      <FaRegCalendarCheck />
    </div>

    <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
      Aucun créneau à afficher
    </h3>

    <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
      Aucun créneau ne correspond au filtre sélectionné ou les horaires n’ont pas
      encore été renseignés.
    </p>
  </div>
);

const CourseItem = ({ course }) => (
  <li className="group/course relative overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-1 hover:shadow-lg dark:border-white/10 dark:bg-slate-950">
    <div
      className={`absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b ${course.colorGradient}`}
    />

    <div className="pl-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 className="font-barlow text-xl font-bold text-slate-950 dark:text-white">
          {course.groupName}
        </h4>

        <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-slate-300">
          <FaClock className="text-primary" />
          {course.time}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {course.ranges.map((range) => (
          <span
            key={range}
            className="rounded-full bg-orange-100 px-3 py-1 text-xs font-bold text-orange-800 dark:bg-orange-500/10 dark:text-orange-300"
          >
            {range}
          </span>
        ))}
      </div>

      {course.teachers && (
        <p className="mt-3 text-sm font-semibold text-slate-500 dark:text-slate-400">
          {course.teachers}
        </p>
      )}
    </div>
  </li>
);

const DayCard = ({ day, courses, isToday }) => (
  <article
    className={`relative overflow-hidden rounded-[2rem] border bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-2xl dark:bg-slate-900 ${
      isToday
        ? "border-orange-300 ring-4 ring-orange-500/10 dark:border-orange-500/40"
        : "border-slate-200 dark:border-white/10"
    }`}
  >
    <div className="mb-5 flex items-center justify-between gap-4">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
          Jour
        </p>
        <h3
          className={`mt-1 font-barlow text-3xl font-bold ${
            isToday
              ? "text-primary"
              : "text-slate-950 dark:text-white"
          }`}
        >
          {day}
        </h3>
      </div>

      <div className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-500 dark:bg-white/10 dark:text-slate-300">
        {courses.length} cours
      </div>
    </div>

    {isToday && (
      <div className="mb-4 rounded-2xl bg-orange-100 px-4 py-2 text-sm font-bold text-orange-800 dark:bg-orange-500/10 dark:text-orange-300">
        Aujourd’hui
      </div>
    )}

    <ul className="space-y-3">
      {courses.map((course, index) => (
        <CourseItem
          key={`${course.groupName}-${course.time}-${index}`}
          course={course}
        />
      ))}
    </ul>
  </article>
);

export default function WeeklySchedule({ groups = [], loading = false }) {
  const schedule = useMemo(() => {
    const data = {};

    const pushCourse = (day, course) => {
      if (!data[day]) {
        data[day] = [];
      }

      data[day].push(course);
    };

    groups.forEach((group) => {
      [group?.horaire1, group?.horaire2].filter(Boolean).forEach((horaire) => {
        const parsed = parseHoraire(horaire);

        if (!parsed?.day) return;

        pushCourse(parsed.day, {
          time: parsed.time,
          startMin: parsed.startMin,
          groupName: group?.nom || "Groupe",
          colorGradient: getColorGradient(group?.color_groupe),
          ranges: tokenizeRanges(group?.annees_naissance),
          teachers: getTeachers(group),
        });
      });
    });

    Object.keys(data).forEach((day) => {
      data[day].sort((a, b) => a.startMin - b.startMin);
    });

    return data;
  }, [groups]);

  const orderedDays = useMemo(() => {
    return DAY_LABELS.filter((day) => schedule[day]?.length);
  }, [schedule]);

  const todayLabel = getTodayLabel();

  return (
    <section className="relative overflow-hidden bg-slate-50 py-20 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-28">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.12),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.13)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.13)_1px,transparent_1px)] bg-[size:42px_42px] opacity-35 dark:opacity-15" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaCalendarAlt />
            Planning hebdomadaire
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Planning{" "}
            <span className="text-primary">
              des cours
            </span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Visualisez les créneaux de la semaine, classés par jour et triés par
            heure de début.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {loading &&
            Array.from({ length: 6 }).map((_, index) => (
              <SkeletonDay key={`schedule-skeleton-${index}`} />
            ))}

          {!loading && orderedDays.length === 0 && <EmptyState />}

          {!loading &&
            orderedDays.map((day, index) => (
              <Reveal
                key={day}
                variant="zoom"
                delay={index * 70}
              >
                <DayCard
                  day={day}
                  courses={schedule[day]}
                  isToday={day === todayLabel}
                />
              </Reveal>
            ))}
        </div>
      </div>
    </section>
  );
}